// import { useParams } from 'react-router-dom'; // Removed unused import to fix lint error
// However, the task says "Centralize public routes". If we are using the new publicRoutes, we probably use a manual mapping or the existing router passes specific component.
// The old Router.tsx renders `<SolutionPage solutionId={path.replace('solutions/', '')} />`.
// I'll keep the prop interface compatible.

import PublicLayout from '../../components/layouts/PublicLayout';
// import OptimizedImage from '../../components/common/OptimizedImage'; // Unused
import { useSEO, generateBreadcrumbSchema, generateServiceSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { Check, Target, ArrowRight } from 'lucide-react';
// import './SolutionPage.css'; // Removed

interface SolutionPageProps {
  solutionId?: string; // Optional if using useParams eventually
}

// FAQ Schema generator
const generateFAQSchema = (items: any[]) => {
  if (!items || items.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
};

const SolutionPage: React.FC<SolutionPageProps> = ({ solutionId }) => {
  const { t, currentLanguage } = usePublicTranslation();

  // If we were using react-router: const { id } = useParams();
  // But current router passes props.
  // Let's support both for future proofing.
  // const id = solutionId;

  // Note: solutionId passed from Router "solutions/markets" -> "markets"
  const id = solutionId || 'markets'; // Default or Error?

  const items = t.solutions?.items as Record<string, any> | undefined;
  const solution = items?.[id];
  const faqItems = t?.faq?.solutions?.items || [];

  // Setup SEO with breadcrumbs and service schema
  useSEO({
    title: solution ? `${solution.title} - NexuCapital` : 'Solução - NexuCapital',
    description: solution?.description || 'Soluções NexuCapital',
    path: `/solutions/${id}`,
    language: currentLanguage as 'pt-br' | 'en' | 'es',
    structuredData: solution ? [
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Soluções', url: '/solutions' },
        { name: solution.title, url: `/solutions/${id}` }
      ]),
      generateWebPageSchema({
        name: `${solution.title} - NexuCapital`,
        description: solution.description,
        url: `/solutions/${id}`,
        datePublished: "2025-01-20",
        dateModified: "2025-01-20"
      }),
      generateServiceSchema({
        name: solution.title,
        description: solution.fullDescription || solution.description,
        url: `/solutions/${id}`,
        areaServed: ['Brasil', 'Internacional']
      }),
      generateFAQSchema(faqItems)
    ].filter(Boolean) : undefined
  });

  if (!solution) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl font-serif font-bold text-dark mb-4">Solução não encontrada</h1>
          <p className="text-gray-600 mb-8">A solução solicitada não existe ou foi removida.</p>
          <a href="/" className="bg-primary text-white px-6 py-2 rounded-full hover:bg-primary-dark transition-colors">
            Voltar ao início
          </a>
        </div>
      </PublicLayout>
    );
  }

  const handleContactClick = () => {
    const subject = encodeURIComponent(`Consulta sobre ${solution.title}`);
    const body = encodeURIComponent(`Olá,\n\nGostaria de mais informações sobre ${solution.title}.`);
    window.location.href = `mailto:contato@nexucapital.com.br?subject=${subject}&body=${body}`;
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative bg-dark text-white py-24 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          {/* Background Image logic: use solution image or generic */}
          {solution.image && <img src={solution.image} alt="" className="w-full h-full object-cover grayscale" />}
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-secondary/20 text-secondary px-3 py-1 rounded-full text-sm font-semibold mb-6">
              Soluções NexuCapital
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6">
              {solution.title}
              {solution.registered && <sup className="text-2xl align-top ml-1">®</sup>}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-2xl">
              {solution.fullDescription || solution.description}
            </p>
            <button
              onClick={handleContactClick}
              className="bg-secondary hover:bg-secondary-dark text-white px-8 py-3 rounded-full font-semibold transition-transform transform hover:scale-105 shadow-lg flex items-center"
            >
              Fale com especialista
              <ArrowRight size={18} className="ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      {solution.features && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-serif font-bold text-dark mb-12 text-center">Principais Funcionalidades</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {solution.features.map((feature: string, index: number) => (
                <div key={index} className="flex items-start p-6 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-md">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary flex-shrink-0 mr-4">
                    <Check size={20} />
                  </div>
                  <span className="text-gray-700 font-medium pt-2">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {solution.benefits && (
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-serif font-bold text-dark mb-12 text-center">Benefícios</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {solution.benefits.map((benefit: string, index: number) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-sm text-center card-hover">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center text-secondary mx-auto mb-4">
                    <Target size={24} />
                  </div>
                  <p className="text-gray-800 font-semibold">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-primary text-white text-center">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">Pronto para começar?</h2>
            <p className="text-xl text-white/90 mb-10">Entre em contato com nossa equipe especializada ou acesse nossa plataforma.</p>
            <button
              onClick={handleContactClick}
              className="bg-white text-primary hover:bg-gray-100 px-8 py-3 rounded-full font-bold text-lg transition-transform transform hover:scale-105 shadow-xl"
            >
              Solicitar demonstração
            </button>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
};

export default SolutionPage;
